import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ErrorMessages, Question, Rules } from '../../config/types';
import { textinputMaxLength } from '../../constants';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  containsReservedCharacters,
  getInputRules,
  groupValidation,
  validateField,
} from '../../utils/form-utils';
import { sanitizeNumericPaste } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';
import Input from './TextInput';

type Props = {
  question?: Question;
  name: string;
  title: string;
  width?: 'full' | 'three-quarters' | 'two-thirds' | 'one-half' | 'one-third' | 'one-quarter';
  rules?: Rules;
  txt: string;
  errorMessages?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
  inputHeading?: string;
} & ComponentPropsWithoutRef<'input'>;

const InstanceInputField = (props: Props) => {
  const {
    question,
    name,
    title,
    rules,
    disabled,
    defaultValue,
    errorMessages,
    detailsHeading,
    detailsContent,
    inputHeading,
    txt,
  } = props;

  //  set suffix text default to "Instances"
  const suffixText = (txt ?? '').trim() || 'Instances';
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { data } = useFormData();

  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textinputMaxLength) }),
    [rules, title],
  );

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hooksFormRegister = register(name, {
    validate: (value: string) => {
      return chainValidations([
        (() => {
          const base = validateField(title, inputRules, value, errorMessages);
          if (typeof base === 'string') return base; // only strings are errors

          // Integer-only validation when numeric rule is set
          if (rules?.number) {
            if (value === '' || value === undefined || value === null) return true; // optional ok
            const ok = /^\d+$/.test(String(value));
            return ok || errorMessages?.number || 'Enter a whole number';
          }

          return true;
        })(),
        groupValidation({
          value: { [name]: value },
          answers: data?.answers,
          question,
        }),
      ]);
    },
  });

  const { error } = getFieldState(name);

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-form-group">
        {inputHeading && (
          <label className="govuk-label" htmlFor="instances">
            {inputHeading}
          </label>
        )}
        <div className="govuk-input__wrapper">
          <Input
            {...props}
            {...hooksFormRegister}
            disabled={disabled ?? false}
            /* Make numeric-only like date inputs */
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            step={1}
            min={0}
            onKeyDown={(e) => {
              // Always enforce reserved character rules
              if (
                e.key.length === 1 &&
                containsReservedCharacters(e.key, inputRules.reservedCharacters)
              ) {
                e.preventDefault();
                return;
              }

              // Block non-digits (covers browsers that allow e, E, ., - on number inputs)
              const allowedNav = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'Tab',
                'Home',
                'End',
              ];
              if (allowedNav.includes(e.key) || e.ctrlKey || e.metaKey) return;

              if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const target = e.currentTarget;
              const selectionStart = target.selectionStart ?? 0;
              const selectionEnd = target.selectionEnd ?? selectionStart;
              const { nextValue, acceptedPaste } = sanitizeNumericPaste(
                target.value,
                e.clipboardData.getData('text'),
                selectionStart,
                selectionEnd,
              );

              if (nextValue === target.value) {
                return;
              }

              const valueSetter =
                Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
              valueSetter?.call(target, nextValue);
              setValue(name, nextValue, { shouldDirty: true, shouldValidate: true });

              requestAnimationFrame(() => {
                try {
                  const nextCursor = selectionStart + acceptedPaste.length;
                  target.setSelectionRange(nextCursor, nextCursor);
                } catch (_error) {
                  // Number inputs do not always support setting the selection range.
                }
              });
            }}
            numeric={true}
            width="one-quarter"
          />
          <div className="govuk-input__suffix" aria-hidden="true">
            {suffixText}
          </div>
        </div>
      </div>
    </>
  );
};

InstanceInputField.displayName = 'InstanceInputField';

export default InstanceInputField;
